import { useState } from "react";
import { PIPELINE_TEMPLATES } from "@capture/shared/types";
import type { Surface } from "@capture/shared";
import { DOCUMENT_CATEGORIES } from "../lib/document-categories";

/** Minimal shape required for the category dropdown — avoids importing the full hook type. */
export interface CategoryOption {
  id: string;
  name: string;
}

interface CreateJobTypeDialogProps {
  onSubmit: (data: {
    name: string;
    description?: string;
    documentCategory?: string;
    templateId?: string;
    categoryId?: string;
    surface?: Surface;
  }) => Promise<void>;
  onClose: () => void;
  isSubmitting: boolean;
  /** Ordered list of tenant categories for the Job Type Category picker. */
  categories?: CategoryOption[];
  /** SP1 §6 — when false the Surface control is HIDDEN entirely (not disabled). */
  remittanceEntitled?: boolean;
}

// F-003 (2026-07-08) — the document-category list now lives in a shared module
// so the create dialog and the Job Config General tab stay in lock-step. Kept
// as a `CATEGORIES` re-export for the existing R-99 taxonomy test.
export { DOCUMENT_CATEGORIES as CATEGORIES } from "../lib/document-categories";

export function CreateJobTypeDialog({ onSubmit, onClose, isSubmitting, categories = [], remittanceEntitled = false }: CreateJobTypeDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [templateId, setTemplateId] = useState("standard");
  const [surface, setSurface] = useState<Surface>("standard");
  const [nameError, setNameError] = useState<string | null>(null);

  // SP3 §2 — a remittance job offered the "standard" (or any non-remittance)
  // template would dead-end at the publish wall (remittance_pipeline_invalid
  // / remittance_config_invalid never satisfied), and a standard job offered
  // a remittance template would 409 immediately on apply-template. Scope the
  // picker to templates whose `surface` marker matches the chosen surface.
  const availableTemplates = Object.values(PIPELINE_TEMPLATES).filter((t) =>
    surface === "remittance" ? t.surface === "remittance" : t.surface === undefined,
  );

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (nameError) setNameError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setNameError(null);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        documentCategory: category || undefined,
        templateId: templateId || undefined,
        categoryId: categoryId || undefined,
        ...(remittanceEntitled && surface !== "standard" ? { surface } : {}),
      });
    } catch (err) {
      const apiErr = err as { status?: number; body?: { error?: string } | null } | null;
      if (
        apiErr?.status === 409 &&
        apiErr.body?.error === "duplicate_name"
      ) {
        setNameError("A Job Type with that name already exists in this tenant.");
        return;
      }
      throw err;
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <form
        className="dialog-panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h2 className="dialog-title">New Job Type</h2>

        <div className="dialog-field">
          <label className="dialog-label">Name *</label>
          <input
            className="rb-input"
            type="text"
            value={name}
            onChange={handleNameChange}
            placeholder="e.g., Invoice Processing"
            autoFocus
          />
          {nameError && (
            <span className="dialog-field-error">{nameError}</span>
          )}
        </div>

        <div className="dialog-field">
          <label className="dialog-label">Description</label>
          <textarea
            className="rb-input dialog-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            rows={2}
          />
        </div>

        <div className="dialog-field">
          {/* F-003 — "Document Category" matches the Job Config General tab
           * label (the document TYPE: Form / Government Form / …). */}
          <label className="dialog-label">Document Category</label>
          <select
            className="rb-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {DOCUMENT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c ? c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "-- Select --"}
              </option>
            ))}
          </select>
        </div>

        <div className="dialog-field">
          {/* F-003 — "Group" is an optional grouping folder for the job-types
           * list, distinct from the Document Category above. */}
          <label className="dialog-label" htmlFor="jt-category-id">Group</label>
          <select
            id="jt-category-id"
            className="rb-select"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">— None —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className="rb-hint">
            Optional folder for organizing the job-types list — not the document type.
          </span>
        </div>

        <div className="dialog-field">
          <label className="dialog-label">Pipeline Template</label>
          <div className="template-grid">
            {availableTemplates.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`template-card ${templateId === t.id ? "template-card--selected" : ""}`}
                onClick={() => setTemplateId(t.id)}
              >
                <div className="template-card-name">{t.name}</div>
                <div className="template-card-desc">{t.description}</div>
                <div className="template-card-steps">
                  {t.steps.map((s, i) => (
                    <span key={i} className="template-step-badge">{s.label.split(" ")[0]}</span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>

        {remittanceEntitled && (
          <div className="dialog-field">
            <label className="dialog-label">Surface</label>
            <div className="template-grid">
              <button
                type="button"
                className={`template-card ${surface === "standard" ? "template-card--selected" : ""}`}
                onClick={() => {
                  setSurface("standard");
                  // SP3 §2 — reset off a template that no longer matches the surface.
                  setTemplateId("standard");
                }}
              >
                <div className="template-card-name">Standard</div>
              </button>
              <button
                type="button"
                className={`template-card ${surface === "remittance" ? "template-card--selected" : ""}`}
                onClick={() => {
                  setSurface("remittance");
                  setTemplateId("remittance");
                }}
              >
                <div className="template-card-name">Remittance</div>
              </button>
            </div>
            <p style={{ fontSize: 12, color: "var(--text-secondary, #8a8a8a)", margin: "6px 0 0" }}>
              Remittance job types add transaction grouping, balancing, and payment
              processing. This can't be changed after creation.
            </p>
          </div>
        )}

        <div className="rb-footer">
          <button type="button" className="rb-btn rb-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="rb-btn rb-btn-save"
            disabled={!name.trim() || isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}
